import { archiveMedicationRecord, saveMedicationRecord } from '../../components/Dashboard/shared/medicationPersistence';
import { saveTimelineFactEntry } from '../../services/timelineFactService';
import { buildCareSourceMedicationReviewCandidate } from '../../utils/medicationReviewCandidate';
import {
  RECEIPT_ACTIONS,
  writeDecisionReceipt,
} from './reviewableDecisionReceipts';

const saveHistoricalMedicationTimelineEntry = async ({ candidate = {}, childId = '', snapshot = {} } = {}) => {
  if (!snapshot.date || !Array.isArray(snapshot.medications) || !snapshot.medications.length) {
    throw new Error('Confirm the historical date and medications before saving.');
  }
  const medicationSummary = snapshot.medications
    .map((entry) => [entry.name, entry.dose, entry.unit, entry.schedule].filter(Boolean).join(' '))
    .join('; ');
  return saveTimelineFactEntry({
    childId,
    factData: {
      date: `${snapshot.date}T12:00:00`,
      label: `Medications at discharge: ${medicationSummary}`,
      sourceText: candidate.sourceText || candidate.extracted?.sourceText || '',
      dateText: snapshot.dateText || '',
      dateApproximate: Boolean(snapshot.dateApproximate),
      medications: snapshot.medications,
    },
    sourceNote: {
      id: candidate.origin?.id || null,
      collection: candidate.origin?.collection || '',
      path: candidate.origin?.collection && candidate.origin?.id
        ? `${candidate.origin.collection}/${candidate.origin.id}`
        : '',
    },
  });
};

const saveCompletedMedicationTimelineEntry = async ({ candidate = {}, childId = '', medication = {} } = {}) => {
  const completion = medication.completedCourse || candidate.extracted?.completedCourse || {};
  if (!completion.effectiveDate) {
    throw new Error('Confirm when the medication course was completed.');
  }
  const medicationName = String(medication.name || candidate.label || '').trim();
  const dose = [medication.dose, medication.unit].filter(Boolean).join(' ').trim();
  const schedule = String(medication.sourceScheduleText || '').trim();
  const summary = [medicationName, dose, schedule].filter(Boolean).join(' · ');
  const canonicalMedicationRecord = medication.canonicalMedicationRecord
    || candidate.extracted?.canonicalMedicationRecord
    || null;

  return saveTimelineFactEntry({
    childId,
    factData: {
      date: `${completion.effectiveDate}T12:00:00`,
      label: `${medicationName} course completed`,
      summary: `${summary || medicationName} — ${completion.reason || 'Course complete'}`,
      sourceText: candidate.sourceText || candidate.extracted?.sourceText || '',
      medicationName,
      medicationDose: medication.dose || '',
      medicationUnit: medication.unit || '',
      medicationSchedule: schedule,
      intervalHours: medication.intervalHours || canonicalMedicationRecord?.schedule?.intervalHours || null,
      durationDays: medication.durationDays || canonicalMedicationRecord?.schedule?.durationDays || null,
      canonicalMedicationRecord,
      medicationChangeType: 'course_completed',
      effectiveDateText: completion.effectiveDateText || '',
      effectiveDateCertainty: canonicalMedicationRecord?.effective?.certainty || '',
      dateApproximate: canonicalMedicationRecord?.effective?.certainty === 'approximate',
    },
    sourceNote: {
      id: candidate.origin?.id || null,
      collection: candidate.origin?.collection || '',
      path: candidate.origin?.collection && candidate.origin?.id
        ? `${candidate.origin.collection}/${candidate.origin.id}`
        : '',
    },
  });
};

const saveUnmatchedTemporaryHoldTimelineEntry = async ({ candidate = {}, childId = '', medication = {} } = {}) => {
  const hold = medication.temporaryHold || candidate.extracted?.temporaryHold || {};
  if (!hold.startDate || !hold.resumeDate) {
    throw new Error('Confirm the temporary hold dates before saving.');
  }
  const medicationName = String(medication.name || candidate.label || '').trim();
  return saveTimelineFactEntry({
    childId,
    factData: {
      date: `${hold.startDate}T12:00:00`,
      label: `${medicationName} temporarily held`,
      summary: `${medicationName} held until ${hold.resumeDate}${hold.reason ? ` — ${hold.reason}` : ''}`,
      sourceText: candidate.sourceText || candidate.extracted?.sourceText || '',
      medicationName,
      medicationChangeType: 'temporary_hold',
      temporaryHold: hold,
    },
    sourceNote: {
      id: candidate.origin?.id || null,
      collection: candidate.origin?.collection || '',
      path: candidate.origin?.collection && candidate.origin?.id
        ? `${candidate.origin.collection}/${candidate.origin.id}`
        : '',
    },
  });
};

export const completeMedicationCandidateMasterListDecision = async ({
  candidate = {},
  childId = '',
  medication = {},
  previousMedication = null,
  decisionAction = 'add_new',
  targetMedication = null,
  receiptAction = RECEIPT_ACTIONS.RESOLVED,
  writeReceipt = true,
  createTimelineEvent = false,
  audit = {},
} = {}) => {
  const cleanDecisionAction = String(decisionAction || '').trim();
  const candidateChildId = String(candidate.childId || candidate.origin?.childId || '').trim();
  const cleanChildId = String(childId || '').trim();
  if (!cleanChildId || (candidateChildId && candidateChildId !== cleanChildId)) {
    throw new Error('Medication review does not belong to the selected profile.');
  }
  let savedMedication = targetMedication;
  let savedTargetCollection = 'medications';

  if (cleanDecisionAction === 'historical_snapshot') {
    const snapshot = medication?.historicalSnapshot || candidate?.extracted?.historicalSnapshot || {};
    savedMedication = await saveHistoricalMedicationTimelineEntry({ candidate, childId: cleanChildId, snapshot });
    savedTargetCollection = 'dailyLogs';
  } else if (cleanDecisionAction === 'add_current_snapshot') {
    const snapshot = medication?.historicalSnapshot || candidate?.extracted?.historicalSnapshot || {};
    const medications = Array.isArray(snapshot.medications) ? snapshot.medications : [];
    if (!medications.length) throw new Error('Review the medications before adding them.');
    const saved = [];
    for (const [index, entry] of medications.entries()) {
      const record = await saveMedicationRecord({
        childId: cleanChildId,
        medication: {
          id: entry.id || `${candidate.candidateId || candidate.key}-current-${index}`,
          name: entry.name,
          dose: entry.dose,
          unit: entry.unit,
          sourceScheduleText: entry.schedule,
          schedules: [{ id: `historical-current-${index}`, dose: entry.dose, unit: entry.unit, time: '' }],
          sourceDocuments: [{ sourceId: candidate.origin?.id || '', title: 'Historical medication source' }],
        },
        status: 'saved',
        createTimelineEvent: false,
      });
      saved.push(record);
    }
    const timelineEntry = await saveHistoricalMedicationTimelineEntry({ candidate, childId: cleanChildId, snapshot });
    savedMedication = {
      id: saved.map((entry) => entry.id).filter(Boolean).join(','),
      medications: saved,
      timelineEntryId: timelineEntry.id,
    };
  } else if (cleanDecisionAction === 'discontinue' && previousMedication?.id) {
    const discontinuingProvider = String(medication?.discontinuation?.provider || '').trim();
    savedMedication = await archiveMedicationRecord({
      childId: cleanChildId,
      medication: {
        ...previousMedication,
        ...(discontinuingProvider ? { prescribingDoctor: discontinuingProvider } : {}),
      },
      archived: true,
      effectiveDate: medication?.discontinuation?.effectiveDate || '',
      createTimelineEvent,
    });
  } else if (cleanDecisionAction === 'complete_course') {
    const completion = medication?.completedCourse || candidate?.extracted?.completedCourse || {};
    if (previousMedication?.id) {
      savedMedication = await archiveMedicationRecord({
        childId: cleanChildId,
        medication: {
          ...previousMedication,
          ...medication,
          id: previousMedication.id,
          childId: previousMedication.childId || cleanChildId,
          medicationPlanAction: 'complete_course',
          completionReason: completion.reason || 'Course complete',
          completedCourse: completion,
        },
        archived: true,
        effectiveDate: completion.effectiveDate || '',
        createTimelineEvent: true,
      });
    } else {
      const completionHistory = {
        id: `${candidate.candidateId || candidate.key || medication.id || 'medication'}-course-completed`,
        type: 'course_completed',
        effectiveDate: completion.effectiveDate,
        reason: completion.reason || 'Course complete',
        summary: `${medication.name || candidate.label || 'Medication'} course completed`,
        sourceId: candidate.origin?.id || '',
        sourceTitle: 'Medication source',
      };
      savedMedication = await saveMedicationRecord({
        childId: cleanChildId,
        medication: {
          ...medication,
          isArchived: true,
          effectiveTo: completion.effectiveDate,
          medicationPlanAction: 'complete_course',
          completionReason: completion.reason || 'Course complete',
          completedCourse: completion,
          planHistory: [
            ...(Array.isArray(medication.planHistory) ? medication.planHistory : []),
            completionHistory,
          ],
        },
        status: 'archived',
        createTimelineEvent: false,
      });
      const timelineEntry = await saveCompletedMedicationTimelineEntry({
        candidate,
        childId: cleanChildId,
        medication,
      });
      savedMedication = { ...savedMedication, timelineEntryId: timelineEntry.id };
    }
  } else if (cleanDecisionAction === 'temporary_hold' && !previousMedication?.id) {
    savedMedication = await saveUnmatchedTemporaryHoldTimelineEntry({
      candidate,
      childId: cleanChildId,
      medication,
    });
    savedTargetCollection = 'dailyLogs';
  } else if (['add_new', 'update_existing', 'temporary_hold'].includes(cleanDecisionAction)) {
    const shouldCreateTimelineEvent = cleanDecisionAction === 'temporary_hold'
      ? true
      : createTimelineEvent;
    savedMedication = await saveMedicationRecord({
      childId: cleanChildId,
      medication,
      previousMedication: cleanDecisionAction === 'add_new' ? null : previousMedication,
      preserveDoseVersion: cleanDecisionAction === 'temporary_hold',
      status: 'saved',
      createTimelineEvent: shouldCreateTimelineEvent,
    });
  }

  if (!savedMedication?.id && !['ignore', 'discontinue'].includes(cleanDecisionAction)) {
    throw new Error('Medication decision did not produce a medication record.');
  }

  if (writeReceipt) {
    await writeDecisionReceipt({
      candidate,
      action: cleanDecisionAction === 'ignore' ? RECEIPT_ACTIONS.IGNORED : receiptAction,
      decisionAction: cleanDecisionAction,
      target: savedMedication?.id ? {
        collection: savedTargetCollection,
        id: savedMedication.id,
      } : null,
      audit: {
        medicationName: savedMedication?.name || savedMedication?.medicationName || medication?.name || '',
        historicalMedicationCount: medication?.historicalSnapshot?.medications?.length || 0,
        ...audit,
      },
    });
  }

  return savedMedication || (cleanDecisionAction === 'discontinue' ? { instructionReviewed: true } : null);
};

export const completeCareSourceMedicationCandidateDecision = async ({
  candidate = {},
  source = {},
  medication = {},
  previousMedication = null,
  decisionAction = 'add_new',
  targetMedication = null,
  receiptAction = RECEIPT_ACTIONS.RESOLVED,
  writeReceipt = true,
  createTimelineEvent = false,
  audit = {},
} = {}) => completeMedicationCandidateMasterListDecision({
  candidate: buildCareSourceMedicationReviewCandidate({ candidate, source }),
  childId: source.childId || '',
  medication,
  previousMedication,
  decisionAction,
  targetMedication,
  receiptAction,
  writeReceipt,
  createTimelineEvent,
  audit,
});
